/**
 * Decides whether a tooltip's content actually needs pushing to tippy.
 *
 * Components with a tip update it from `onupdate`, which Mithril runs on EVERY redraw. Tippy's
 * `setProps` re-evaluates its whole prop set and writes attributes on the reference element, so doing it
 * unconditionally is expensive even when the tip text has not changed. In a portal render profile
 * (`bun cli profile portal / --scenario redraw`) that was ~8% of all JS self time, and 4200 of the ~4260
 * DOM attribute writes across 60 redraws of a page where nothing changed.
 *
 * There are two levels of skip, because resolving the content is itself costly for some callers — `Icon`
 * renders a JSX tip into a throwaway div via `m.render` purely to get an HTML string:
 *
 *   1. A non-function source identical to last time cannot have changed → skip without resolving at all.
 *   2. Otherwise resolve, then compare the RESULT → skip if it came out the same.
 *
 * A function source is always resolved, since only its return value reveals whether it changed. That
 * keeps dynamic tips correct: `() => count.value` still updates the moment its output differs.
 */
export class TipMemo<TContent = unknown> {
    private source: unknown
    private content: TContent | undefined
    private primed = false

    /**
     * Record what tippy was actually constructed with, so the first `onupdate` compares against reality
     * rather than against `undefined` (which would force one redundant push).
     */
    prime(source: unknown, content: TContent) {
        this.source = source
        this.content = content
        this.primed = true
    }

    /**
     * Returns the content to push, or `null` when tippy can be left alone.
     *
     * `resolve` is invoked only when the source might have changed, so callers can put expensive
     * rendering inside it. Returned as a wrapper object rather than a bare value so that a legitimately
     * null/empty tip is still distinguishable from "nothing to do".
     */
    next(source: unknown, resolve: () => TContent): {content: TContent} | null {
        // Never primed (tippy built without going through `prime`): resolve once and push, so a caller
        // that forgets to prime degrades to the old always-update behaviour rather than never updating.
        if (this.primed && typeof source !== 'function' && source === this.source) {
            return null
        }

        const content = resolve()
        if (this.primed && content === this.content) {
            this.source = source
            return null
        }

        this.source = source
        this.content = content
        this.primed = true
        return {content}
    }
}
